'use client';
import { Box, Grid, IconButton, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { CENTERED_ICON, COLORS, MOTION, RADIUS } from '../../constants';
import { MaterialIcon, MaterialIconType } from '../materialIcon';

/**
 * Borderless filled card with a round white icon chip and a dark title, used for each group of fields on a detail
 * page (prospect data, address, representative).
 *
 * @param {MaterialIconType} icon - Chip icon.
 * @param {string} title - Section heading, rendered as an h2.
 * @param {React.ReactNode} children - The section's content, usually an InfoGrid.
 * @param {SxProps<Theme>} [sx] - Overrides for the card, e.g. a transparent background around a table.
 * @return {React.JSX.Element} The rendered section.
 */
export const DetailSection = ({
  icon,
  title,
  children,
  sx,
}: {
  icon: MaterialIconType;
  title: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) => (
  <Stack
    component="section"
    sx={[
      {
        gap: 2,
        p: { xs: 2, md: 3 },
        borderRadius: `${RADIUS.xl}px`,
        // Same light grey as the info tiles across the apps
        bgcolor: COLORS.background[0],
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
      <Box
        aria-hidden="true"
        sx={{
          ...CENTERED_ICON,
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: COLORS.surface.raised,
        }}
      >
        <MaterialIcon icon={icon} size={20} sx={{ color: COLORS.text.brand }} />
      </Box>
      <Typography variant="h2" component="h2" sx={{ color: COLORS.text.main, fontWeight: 700 }}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Stack>
);

/**
 * Responsive grid for InfoFields.
 *
 * @param {React.ReactNode} children - The InfoFields.
 * @return {React.JSX.Element} The rendered grid.
 */
export const InfoGrid = ({ children }: { children: React.ReactNode }) => (
  <Grid spacing={2} container size="grow" sx={{ flexDirection: 'row', width: '100%' }}>
    {children}
  </Grid>
);

// Round ghost button shared by copy and edit, so both actions sit the same size and spot next to a value
export const INFO_FIELD_ACTION_SX = {
  width: 32,
  height: 32,
  flexShrink: 0,
  color: COLORS.text.brand,
  '&:hover': { bgcolor: COLORS.surface.raised },
} as const;

/**
 * One label/value pair in an InfoGrid, with a copy button and any extra actions on the right. With a mouse the
 * actions stay faded until the field is hovered or focused, so the right edge isn't a column of blue icons; touch
 * screens have no hover and keep them solid. Extra actions get the same fade by carrying the `field-action` class.
 *
 * @param {string} label - Grey label above the value.
 * @param {React.ReactNode} value - A string renders bold and can be copied; any other node renders as is.
 * @param {() => void} [onCopy] - Shows the copy button for a non-empty string value ('-' counts as empty).
 * @param {React.ReactNode} [actions] - Extra buttons after the copy button, e.g. edit.
 * @param {boolean} [large] - Takes the whole row, for long values like addresses.
 * @param {object} [size] - Grid size, a third of the row on desktop by default.
 * @return {React.JSX.Element} The rendered field.
 */
export const InfoField = ({
  label,
  value,
  onCopy,
  actions,
  large,
  size = { xs: 12, sm: 6, md: 4 },
}: {
  label: string;
  value: React.ReactNode;
  onCopy?: () => void;
  actions?: React.ReactNode;
  large?: boolean;
  size?: { xs?: number; sm?: number; md?: number };
}) => {
  const copyable = typeof value === 'string' && value !== '' && value !== '-' && onCopy;
  return (
    <Grid size={large ? 12 : size}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 0.5,
          '@media (hover: hover)': {
            '& .field-action': { opacity: 0.3, transition: `opacity ${MOTION.short}ms` },
            '&:hover .field-action, &:focus-within .field-action': { opacity: 1 },
          },
        }}
      >
        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
          <Typography variant="body2" sx={{ color: COLORS.text.light }}>
            {label}
          </Typography>
          {typeof value === 'string' ? (
            <Typography variant="body1" sx={{ color: COLORS.text.main, fontWeight: 600, wordBreak: 'break-word' }}>
              {value}
            </Typography>
          ) : (
            value
          )}
        </Stack>
        {copyable && (
          <Tooltip title="Copiar" arrow placement="top">
            <IconButton
              className="field-action"
              aria-label={`Copiar ${label.toLowerCase()}`}
              onClick={onCopy}
              sx={INFO_FIELD_ACTION_SX}
            >
              <MaterialIcon icon="content_copy" size={18} aria-hidden="true" />
            </IconButton>
          </Tooltip>
        )}
        {actions}
      </Stack>
    </Grid>
  );
};

/**
 * Brand circle with up to two initials of a name, so a business has a face like the customer avatars.
 *
 * @param {string} [name] - Name to take the initials from.
 * @param {number} [size] - Diameter in px.
 * @return {React.JSX.Element} The rendered avatar.
 */
export const InitialsAvatar = ({ name, size = 56 }: { name?: string; size?: number }) => (
  <Box
    aria-hidden="true"
    sx={{
      ...CENTERED_ICON,
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: COLORS.brand.main,
      typography: 'h2',
      fontSize: Math.round(size * 0.36),
      fontWeight: 700,
      // After the typography shorthand, which brings the h2's own brand color
      color: COLORS.text.inverted,
    }}
  >
    {(name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')}
  </Box>
);
